'use client';

import { FC, useState } from 'react';
import { useController } from 'react-hook-form';

import { Category } from '@/__generated_graphql__/graphql';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { DropdownArrowIcon } from '@/components/ui/Icons/Icons';
import { TypographyVariant } from '@/components/ui/Typography/Typography.types';
import { useCategoriesFilter } from '@/hooks/useCategoriesFilter';

import * as S from './CategoriesFilter.styles';

type CategoryProps = {
  categoryLabelVariant?: TypographyVariant;
};

export const CategoriesFilter: FC<CategoryProps> = ({
  categoryLabelVariant = 'title5'
}) => {
  const { categories, control, getSelectedState, toggleJob, toggleJobsGroup } =
    useCategoriesFilter();

  const [expandedCategory, setExpandedCategory] = useState<string>('');

  const {
    field: { value: selectedCategories }
  } = useController({
    name: 'categories',
    control
  });

  if (!categories) return null;

  const handleToggleCategory = (categoryId: string) =>
    setExpandedCategory(prev => (prev === categoryId ? '' : categoryId));

  return (
    <S.Wrapper data-type="categories-filter">
      {categories.map(category => {
        const jobs: Category[] = category.children ?? [];
        const { allSelected, someSelected } = getSelectedState(
          jobs,
          selectedCategories
        );
        const isExpanded = expandedCategory === category.id;

        return (
          <S.CategoryWrapper
            key={category.id}
            data-open={isExpanded}
            data-type="category"
          >
            <S.CategoryHeader onClick={() => handleToggleCategory(category.id)}>
              <Checkbox
                isIndeterminate={!allSelected && someSelected}
                isChecked={allSelected}
                onChange={() => toggleJobsGroup(category, selectedCategories)}
              />
              <S.CategoryLabel variant={categoryLabelVariant}>
                {category.name}
              </S.CategoryLabel>
              <S.Arrow $isOpen={isExpanded}>
                <DropdownArrowIcon />
              </S.Arrow>
            </S.CategoryHeader>

            <S.JobsWrapper $isOpen={isExpanded} data-type="job">
              {jobs.map(job => (
                <S.JobItem key={job.id}>
                  <Checkbox
                    isChecked={selectedCategories.some(selected => {
                      const isParentId = selected.parentId === null;
                      return isParentId
                        ? String(selected.id) === String(job.parentId)
                        : selected.id === job.id;
                    })}
                    onChange={() => toggleJob(job, selectedCategories)}
                  />
                  <S.JobLabel>{job.name}</S.JobLabel>
                </S.JobItem>
              ))}
            </S.JobsWrapper>
          </S.CategoryWrapper>
        );
      })}
    </S.Wrapper>
  );
};
